/**
 * Login page component
 * Allows users to authenticate with username and password
 */

import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import type { AuthError, LoginResponse } from '@/types/auth';
// US-003 T4: import centralized ROUTES and routing helper (TO-LL-013/TO-LL-014 compliance).
// getPostLoginRoute computes the post-login destination synchronously from user.groups so
// there is no loading state between submit and navigate (AC-4).
import { ROUTES, getPostLoginRoute } from '@/constants/routes';
import logoImage from '@/assets/thinkotter-logo.png';
import loginBg from '@/assets/login-bg.svg';
// google-sign-in/US-001 (T5, AC-001.1): "Continue with Google" entry point on the
// shared login/register page.
import { ContinueWithGoogleButton } from '@/components/auth/ContinueWithGoogleButton';
import { isOAuthConfigured } from '@/lib/amplifyConfig';
// Role-split sign-in (2026-07-29): remembered-door persistence. See that module for why
// the value survives session expiry but not an explicit logout.
import { readRememberedRole, rememberRole, type SignInRole } from '@/lib/signInRole';

// BUG-20260217-105236: Routes that are generic hub pages and should NOT override the default
// /dashboard landing after login. A stale /sessions path saved by AuthGuard (e.g., from
// browser history) must not hijack the intended post-login destination. Only deep-link paths
// like /session/:sessionId should be honoured as redirect targets. - 2026-02-17
// US-003 T4: updated to reference ROUTES constants instead of hardcoded string literals
// (enforces TO-LL-013). The parent fork now lands parents at ROUTES.PARENT_DASHBOARD and
// students at ROUTES.STUDENT_HOME via getPostLoginRoute(). The generic-route filter still
// applies for students only — parents always go to PARENT_DASHBOARD regardless of state.from.
const GENERIC_ROUTES = [ROUTES.SESSIONS, ROUTES.STUDENT_HOME];

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  // Role-split sign-in (2026-07-29). null = the picker is showing.
  // Seeded from localStorage so a returning family does not pay a tap every visit.
  // Written on choose, cleared on EXPLICIT logout (see useAuth.logout) but deliberately
  // NOT on session expiry — an expired session is the same person coming back, whereas
  // deliberately signing out on a shared family device is the moment the other role wants in.
  const [role, setRole] = useState<SignInRole | null>(() => readRememberedRole());

  // Get success message from registration redirect
  const successMessage = location.state?.message;

  const chooseRole = (next: SignInRole) => {
    setRole(next);
    rememberRole(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Call login mutation
    login(
      // `role` is a UX gate only — see the note on LoginRequest.role. Routing after a
      // successful sign-in still derives from the account's Cognito groups below, never
      // from the button the user tapped.
      { username, password, ...(role ? { role } : {}) },
      {
        onSuccess: (data: LoginResponse) => {
          // US-003 T4: group-based post-login routing (AC-1, AC-2, AC-4).
          // getPostLoginRoute is synchronous — no async work, no loading state (AC-4).
          // Parent accounts always land at PARENT_DASHBOARD regardless of state.from;
          // the parent fork ignores deep-link redirection because parents have a single
          // fixed home (security: no open redirect risk for parent accounts).
          const groups = data.user.groups;
          const postLoginDest = getPostLoginRoute(groups);

          if (postLoginDest === ROUTES.PARENT_DASHBOARD) {
            navigate(ROUTES.PARENT_DASHBOARD, { replace: true });
            return;
          }

          // BUG-20260217-105236: Filter out generic hub routes saved by AuthGuard so that
          // stale browser-history paths like /sessions do not override the /dashboard default.
          // Only preserve the 'from' path when it is a genuine deep-link (e.g. /session/:id).
          // US-003 T4: uses ROUTES.STUDENT_HOME instead of hardcoded '/dashboard' (TO-LL-013).
          const fromPath = location.state?.from?.pathname;
          const from =
            fromPath && !GENERIC_ROUTES.includes(fromPath) ? fromPath : ROUTES.STUDENT_HOME;
          navigate(from, { replace: true });
        },
        onError: (err: AuthError) => {
          // If the user is not yet email-verified, redirect to verification page
          // Backend returns 403 FORBIDDEN for unverified Cognito accounts
          if (err.code === 'FORBIDDEN') {
            navigate('/verify-email', {
              state: { username },
              replace: true,
            });
          }
          // For all other errors (UNAUTHORIZED, SERVER_ERROR, etc.),
          // the useAuth hook already sets the error state → rendered below the form.
        },
      }
    );
  };

  const isSubmitDisabled = isLoading || !username || !password;

  return (
    <div
      className="relative flex flex-col min-h-screen items-center justify-center px-3 md:px-4 py-8 md:py-12"
      style={{ backgroundColor: '#fdf8f0' }}
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${loginBg})` }}
        aria-hidden="true"
      />

      {/* Overlay wash for readability */}
      <div className="absolute inset-0 bg-white/60" aria-hidden="true" />

      {/* Content layer */}
      <div className="relative z-10 flex flex-col items-center w-full">
        {/* ThinkOtter Logo and Headline */}
        <div className="mb-6 flex flex-col items-center w-full max-w-md">
          <img src={logoImage} alt="ThinkOtter" className="w-[80%] h-auto" />
          <p className="mt-3 text-base md:text-lg text-gray-700 font-medium text-center">
            Let's learn together today
          </p>
        </div>

        <Card className="w-full max-w-md bg-otter-paper rounded-[20px] shadow-md border-[#E7D8C8]">
          {/* Role-split sign-in (2026-07-29, revised): the header NAMES the door.
              Before this, the only clues you were on the wrong one were indirect — the
              presence of a Google button, whether the field said "Email address" or
              "Username", and a small "Not a parent?" link below the fold on a phone. A
              parent who landed on the student form had to infer it from what was missing.
              Now it is stated outright, and the same emoji from the picker is repeated so
              the choice you just made is visually carried through rather than forgotten. */}
          <CardHeader className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-2xl font-bold text-otter-earth-800">
              {role !== null && (
                <span aria-hidden="true" className="text-xl">
                  {role === 'parent' ? '👤' : '🎒'}
                </span>
              )}
              {role === null
                ? 'Welcome Back'
                : role === 'parent'
                  ? 'Welcome back, parent'
                  : 'Welcome back, student'}
            </CardTitle>
            <CardDescription className="text-otter-earth-700">
              {role === null
                ? "Who's signing in?"
                : role === 'parent'
                  ? 'Sign in to ThinkOtter'
                  : 'Ask your parent if you forget your username'}
            </CardDescription>
          </CardHeader>

          {/* Success message from registration */}
          {successMessage && (
            <div className="mx-6 mb-4 rounded-md bg-green-50 p-3 text-sm text-green-800 border border-green-200">
              {successMessage}
            </div>
          )}

          {/* google-sign-in/US-001 (T5, AC-001.1): "Continue with Google" entry point.
              Moved ABOVE the password form (2026-07-28) so the one-tap path is the first
              thing a returning parent sees. It sits OUTSIDE the <form> now — the divider
              reads "or" between two alternatives, and a control that triggers an OAuth
              redirect does not belong inside a form it never submits. (The button is
              type="button", so this was never a live submit bug — but out here it cannot
              become one.) Whole block is skipped when the OAuth env is not configured
              (T1), so this never blocks password login.
              BUG-20260713-020205 (D2, Option B) / T-7: login-page Google is sign-in-only
              — intent='login' lets OAuthCallbackPage bounce a not-yet-registered
              (consentPending) Google account to /register. */}
          {/* ── Role picker ──────────────────────────────────────────────────
              Role-split sign-in (2026-07-29). Shown only when we have no remembered
              choice, so this is a first-visit screen, not a toll every returning user
              pays. Choosing does NOT authorise anything — it selects which form to show
              and which value to post. Where the user lands afterwards still comes from
              their Cognito groups. */}
          {role === null && (
            <CardContent className="space-y-3">
              <button
                type="button"
                onClick={() => chooseRole('parent')}
                className="w-full flex items-center gap-3 rounded-[14px] border-[1.5px] border-[#E7D8C8] bg-white p-4 text-left transition-colors hover:border-otter-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-otter-orange-500"
              >
                <span
                  aria-hidden="true"
                  className="grid h-11 w-11 flex-none place-items-center rounded-[12px] bg-otter-sand text-xl"
                >
                  👤
                </span>
                <span className="flex flex-col">
                  <span className="font-bold text-otter-earth-800">I'm a parent</span>
                  <span className="text-xs text-gray-500">Email or Google account</span>
                </span>
              </button>

              <button
                type="button"
                onClick={() => chooseRole('student')}
                className="w-full flex items-center gap-3 rounded-[14px] border-[1.5px] border-[#E7D8C8] bg-white p-4 text-left transition-colors hover:border-otter-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-otter-orange-500"
              >
                <span
                  aria-hidden="true"
                  className="grid h-11 w-11 flex-none place-items-center rounded-[12px] bg-otter-sand text-xl"
                >
                  🎒
                </span>
                <span className="flex flex-col">
                  <span className="font-bold text-otter-earth-800">I'm a student</span>
                  <span className="text-xs text-gray-500">Username from your parent</span>
                </span>
              </button>

              <p className="pt-2 text-center text-sm text-gray-500">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="text-otter-earth-700 hover:text-otter-orange-500 hover:underline"
                >
                  Sign up
                </Link>
              </p>
            </CardContent>
          )}

          {/* google-sign-in/US-001 (T5, AC-001.1): "Continue with Google" entry point.
              Above the password form since 2026-07-28, and OUTSIDE the <form> — the divider
              reads "or" between two alternatives, and a control that fires an OAuth redirect
              does not belong in a form it never submits.
              2026-07-29: now PARENT-ONLY. Students cannot use Google at all — their accounts
              are created by a parent with a username and a placeholder address — so showing
              them the button only offered a door that could never open.
              BUG-20260713-020205 (D2, Option B) / T-7: intent='login' lets OAuthCallbackPage
              bounce a not-yet-registered (consentPending) Google account to /register. */}
          {role === 'parent' && isOAuthConfigured && (
            <div className="px-6 pb-4 flex flex-col space-y-4">
              <ContinueWithGoogleButton intent="login" />
              <div className="flex items-center gap-3 w-full" aria-hidden="true">
                <div className="h-px flex-1 bg-[#E7D8C8]" />
                <span className="text-xs text-gray-500">or</span>
                <div className="h-px flex-1 bg-[#E7D8C8]" />
              </div>
            </div>
          )}

          {role !== null && (
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {/* THE IDENTIFIER FIELD — ONE input, and it must stay one.
                  Role-split sign-in (2026-07-29): the role swaps this field's label,
                  placeholder and autocomplete hint. It does NOT render a different input
                  per role, and it must never be keyed by role. Two conditionally-rendered
                  forms would unmount and remount the input on every switch, and password
                  managers (iOS Keychain, 1Password) bind at mount — that is how you break
                  autofill for every returning user.
                  It keeps accepting BOTH kinds of value whichever door is chosen: the pool
                  sets AliasAttributes:[email] so a parent can use either, and students hold
                  placeholder addresses ({username}@student.thinkotter.internal) that cannot
                  receive mail, so this can never narrow to email-only. The role changes what
                  we ASK for, never what the backend will accept. */}
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-otter-earth-800">
                    {role === 'parent' ? 'Email address' : 'Username'}{' '}
                    {/* Shown for students only. It is true of usernames, and beside a parent's
                      "Email address" it would read as a warning about their email — sending
                      them hunting for a capitalisation problem that isn't theirs to fix. */}
                    {role === 'student' && (
                      <span className="text-xs font-normal text-gray-500">(case-sensitive)</span>
                    )}
                  </Label>
                  <Input
                    id="username"
                    // type stays "text", NOT "email": this field accepts usernames too, and
                    // type="email" would apply browser validation that rejects them outright.
                    type="text"
                    placeholder={
                      role === 'parent' ? 'Enter your email address' : 'Enter your username'
                    }
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={isLoading}
                    autoFocus
                    // Because type="text" cannot bring the keyboard behaviour along, these are
                    // set explicitly. Mobile keyboards autocapitalise a text input by default,
                    // and Cognito alias matching is CASE-SENSITIVE (verified against the dev
                    // pool) — so a phone helpfully sending "Tom45@gmail.com" for an address
                    // stored lowercase produces "Invalid credentials" on a correct password.
                    // Correct for usernames too, which are case-sensitive and should never
                    // acquire a capital first letter the user did not type.
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    autoComplete="username"
                    className="h-11 md:h-10 border-[#E7D8C8]"
                  />
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-otter-earth-800">
                    Password
                  </Label>
                  <PasswordInput
                    id="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="h-11 md:h-10 border-[#E7D8C8]"
                  />
                </div>

                {/* Error message.
                  ROLE_MISMATCH (2026-07-29) gets its own treatment because it is the one
                  error where the user did nothing wrong — the credentials were correct,
                  they are simply at the wrong door. So it names the right one and offers a
                  one-tap way there, rather than reading as a failure. Being specific is safe
                  precisely because it only appears AFTER correct credentials, so it tells an
                  attacker nothing they could not already confirm. */}
                {error?.code === 'ROLE_MISMATCH' ? (
                  <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-sm font-medium text-amber-800">
                      {role === 'parent'
                        ? 'That looks like a student account. Please use student sign-in.'
                        : 'That looks like a parent account. Please use parent sign-in.'}
                    </p>
                    <button
                      type="button"
                      onClick={() => chooseRole(role === 'parent' ? 'student' : 'parent')}
                      className="text-sm font-semibold text-otter-earth-700 underline hover:text-otter-orange-500"
                    >
                      {role === 'parent' ? 'Switch to student sign-in' : 'Switch to parent sign-in'}
                    </button>
                  </div>
                ) : (
                  error && <p className="text-sm text-red-500">{error.message}</p>
                )}
              </CardContent>

              <CardFooter className="flex flex-col space-y-4">
                <Button
                  type="submit"
                  variant="warm"
                  className="w-full rounded-[10px]"
                  disabled={isSubmitDisabled}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>

                {/* The Google entry point used to sit here, below Sign In; it now renders
                  above the form (2026-07-28). See the block above the <form>. */}

                {/* Always available, so a wrong choice is never a trap — including on a
                  shared device where the remembered role belongs to somebody else. */}
                <button
                  type="button"
                  onClick={() => chooseRole(role === 'parent' ? 'student' : 'parent')}
                  className="text-sm font-semibold text-otter-earth-700 hover:text-otter-orange-500 hover:underline"
                >
                  {role === 'parent' ? '← Not a parent?' : '← Not a student?'}
                </button>

                {role === 'parent' && (
                  <>
                    <p className="text-center text-sm text-gray-500">
                      Don't have an account?{' '}
                      <Link
                        to="/register"
                        className="text-otter-earth-700 hover:text-otter-orange-500 hover:underline"
                      >
                        Sign up
                      </Link>
                    </p>
                    <p className="text-center text-sm text-gray-500">
                      Forgot your password?{' '}
                      <Link
                        to="/forgot-password"
                        className="text-otter-earth-700 hover:text-otter-orange-500 hover:underline"
                      >
                        Forgot Password?
                      </Link>
                    </p>
                  </>
                )}

                {/* Students get neither link, and both omissions are deliberate.
                  SIGN UP: children do not self-register — a parent creates the account.
                  FORGOT PASSWORD: reset emails go to {username}@student.thinkotter.internal,
                  which cannot receive mail, and forgot_password returns 200 regardless — so
                  the child would see a success screen and no email would ever arrive. A
                  parent resets it from their dashboard (ResetChildPasswordDialog, reachable
                  in one tap from each child's card). */}
                {role === 'student' && (
                  <p className="text-center text-sm text-gray-500">
                    Forgotten your password? Ask your parent to reset it for you.
                  </p>
                )}
              </CardFooter>
            </form>
          )}
        </Card>

        {/* Value proposition text */}
        <p className="mt-6 text-center text-sm text-gray-500 font-medium">
          Short passages &bull; Smart hints &bull; Earn pebbles
        </p>
      </div>
    </div>
  );
}
